import {
    computed,
    type ComputedRef,
    onMounted,
    type Ref,
    watch,
    type WritableComputedRef,
} from 'vue'
import md5 from 'md5'
import { entries, fromEntries, isArray, isDeepEqual } from 'remeda'

import {
    getProductCategory,
    useSegmentProductListAnalytics,
} from '@/views/product/composables/analytics/useSegmentProductAnalytics'
import {
    CatalogueSortOrder,
    type RegionCode,
    type CatalogueEngagementRingsFilterInput,
    type CatalogueJewelleriesFilterInput,
    type CatalogueReadyToGoRingsFilterInput,
    type CatalogueWeddingRingFilterInput,
    type ProductListAnalyticsFragment,
    type CatalogueJewelleriesBraceletsFilterInput,
    type CatalogueJewelleriesEarringsFilterInput,
    type CatalogueJewelleriesNecklacesFilterInput,
    type CatalogueJewelleriesRingsFilterInput,
} from '@/graphql/api'
import type { JewelleryProductType } from '@/router'
import { useRegionPlugin } from '@/plugins/region'
import { assertNever } from '@/helpers'
import { useSegment } from '@/third-party/segment.ts'

// Guarantees correct sorting in JSON.stringify()
const replacer = (_: string, value: any) =>
    value instanceof Object && !(value instanceof Array)
        ? Object.keys(value)
              .sort()
              .reduce(
                  (sorted, key) => {
                      sorted[key] = value[key]
                      return sorted
                  },
                  {} as Record<string, any>
              )
        : value

function getAnalyticsProductListCategory(
    product:
        | Pick<ProductListAnalyticsFragment, '__typename'>[]
        | undefined
        | null,
    jewelleries?: { productType: JewelleryProductType | undefined }
) {
    if (jewelleries) {
        if (!jewelleries.productType) {
            return 'Jewellery'
        }

        switch (jewelleries.productType) {
            case 'BRACELET':
                return 'Bracelet'
            case 'EARRINGS':
                return 'Earring'
            case 'NECKLACE':
                return 'Necklace'
            case 'RING':
                return 'Ring'
            default:
                assertNever(jewelleries.productType, 'Jewellery')
        }
    }

    if (!product?.[0]) {
        return ''
    }

    return getProductCategory(product[0])
}

type SegmentProductCategory = ReturnType<typeof getAnalyticsProductListCategory>

type ProductFilterInput =
    | CatalogueJewelleriesFilterInput
    | CatalogueJewelleriesBraceletsFilterInput
    | CatalogueJewelleriesEarringsFilterInput
    | CatalogueJewelleriesNecklacesFilterInput
    | CatalogueJewelleriesRingsFilterInput
    | CatalogueWeddingRingFilterInput
    | CatalogueReadyToGoRingsFilterInput
    | CatalogueEngagementRingsFilterInput

function generateAnalyticsProductListId(
    productCategory: SegmentProductCategory,
    filterInput:
        | ProductFilterInput
        | {
              price?: Pick<
                  NonNullable<ProductFilterInput['price']>,
                  'min' | 'max'
              > | null
          },
    sortBy: CatalogueSortOrder,
    page: number,
    regionCode: RegionCode
) {
    const trimEmpty = fromEntries(
        entries(filterInput).filter(([, value]) => {
            if (!value) {
                return false
            }

            if (isArray(value)) {
                return value.length > 0
            }

            return true
        })
    )

    return `${md5(`${productCategory}-${JSON.stringify(trimEmpty, replacer)}`)}_page_${page}_${sortBy}_${regionCode}`
}

interface ProductListAnalytics {
    trackClicked: (product: ProductListAnalyticsFragment, index: number) => void
}

export function useProductListAnalytics(
    products: Readonly<Ref<ProductListAnalyticsFragment[] | undefined | null>>,
    filterInput:
        | ComputedRef<
              | CatalogueJewelleriesFilterInput
              | CatalogueWeddingRingFilterInput
              | CatalogueReadyToGoRingsFilterInput
          >
        | WritableComputedRef<CatalogueEngagementRingsFilterInput>,
    categoryFacets: ComputedRef<(string | undefined)[]>,
    sortBy: Readonly<Ref<CatalogueSortOrder>>,
    page: Readonly<Ref<number>>,
    jewelleries?: ComputedRef<{
        productType: JewelleryProductType | undefined
    }>
): ProductListAnalytics {
    const { regionCode } = useRegionPlugin()

    const productCategory = computed(() =>
        getAnalyticsProductListCategory(products.value, jewelleries?.value)
    )

    const filterInputNomralizedPrice = computed(() => ({
        ...filterInput.value,
        price:
            filterInput.value?.price?.min && filterInput.value.price.max
                ? {
                      min: filterInput.value.price.min,
                      max: filterInput.value.price.max,
                  }
                : null,
    }))

    const listId = computed(() =>
        generateAnalyticsProductListId(
            productCategory.value,
            filterInputNomralizedPrice.value,
            sortBy.value,
            page.value,
            regionCode.value
        )
    )

    const priceFacet = computed(() =>
        filterInputNomralizedPrice.value.price
            ? ` ${filterInputNomralizedPrice.value.price.min}-${filterInputNomralizedPrice.value.price.max}`
            : ''
    )

    const categoryString = computed(() =>
        `${categoryFacets.value.filter(Boolean).join(' ')} ${productCategory.value}${priceFacet.value} ${sortBy.value} ${regionCode.value}`.trim()
    )

    const serialize = useSegmentProductListAnalytics(
        listId,
        categoryString,
        sortBy,
        page
    )

    const { track } = useSegment()

    const trackViewed = () => {
        if (!products.value) {
            return
        }

        track('Product List Viewed', serialize(products.value))
    }

    const trackClicked = (
        product: ProductListAnalyticsFragment,
        index: number
    ) => {
        track('Product Clicked', serialize([product], index))
    }

    watch(products, (nP, oldP) => {
        if (isDeepEqual(nP, oldP)) {
            return
        }

        trackViewed()
    })

    onMounted(trackViewed)

    return { trackClicked }
}

if (import.meta.vitest) {
    const { describe, test } = import.meta.vitest

    describe('Generate analytics PLP ID for WR', () => {
        test.concurrent.for([
            [
                'Without filters',
                {},
                '10937f20dfd86f834c7e082c08475175_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: mens',
                { categories: ['MENS'] },
                'a5e1393742b91a269cc7ec22dbbc6b99_page_1_RECOMMENDED_UK',
            ],
            [
                'Metal: platinum',
                { primaryMetalCodes: ['PLATINUM'] },
                '7db43983859e77caa2f4e91fa6fd5ec8_page_1_RECOMMENDED_UK',
            ],
            [
                'Ring style: plain',
                { ringStyles: ['PLAIN'] },
                'ca4d68b00637a76de88d3bba6fb6245e_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: mens, metal: platinum, ring style: plain',
                {
                    categories: ['MENS'],
                    ringStyles: ['PLAIN'],
                    primaryMetalCodes: ['PLATINUM'],
                },
                '8d61da6f5b884ee23bdaf404e3ae8c7f_page_1_RECOMMENDED_UK',
            ],
            [
                'Price: 3199-4697',
                { price: { min: '3199', max: '4697' } },
                '001edc7d6cce86ac965c0da00c025f6e_page_1_RECOMMENDED_UK',
            ],
        ] as [string, CatalogueWeddingRingFilterInput, string][])(
            '%s',
            ([, filterInput, id], { expect }) => {
                expect(
                    generateAnalyticsProductListId(
                        getAnalyticsProductListCategory([
                            {
                                '__typename': 'CatalogueWeddingRing',
                            },
                        ]),
                        filterInput,
                        CatalogueSortOrder.Recommended,
                        1,
                        'UK'
                    )
                ).toBe(id)
            }
        )
    })

    describe('Generate analytics PLP ID for RTG', () => {
        test.concurrent.for([
            [
                'Without filters',
                {},
                'ac35d579f6f6882bae41b5211cae8d04_page_1_RECOMMENDED_UK',
            ],
            [
                'Metal: platinum',
                { primaryMetalCodes: ['PLATINUM'] },
                '8f19b0a091009891e1d51ea923984a64_page_1_RECOMMENDED_UK',
            ],
            [
                'Price: 2089.2-2236.2',
                { price: { min: '2089.2', max: '2236.2' } },
                'aedbeed71401d3170aa3e8c666f66ae4_page_1_RECOMMENDED_UK',
            ],
        ] as [string, CatalogueWeddingRingFilterInput, string][])(
            '%s',
            ([, filterInput, id], { expect }) => {
                expect(
                    generateAnalyticsProductListId(
                        getAnalyticsProductListCategory([
                            {
                                '__typename': 'CatalogueReadyToGoRing',
                            },
                        ]),
                        filterInput,
                        CatalogueSortOrder.Recommended,
                        1,
                        'UK'
                    )
                ).toBe(id)
            }
        )
    })

    describe('Generate analytics PLP ID for ER', () => {
        test.concurrent.for([
            [
                'Without filters',
                {},
                '7dfb0bfbe6026bf8b1e88dd740df0540_page_1_RECOMMENDED_UK',
            ],
            [
                'Metal: platinum',
                { primaryMetalCodes: ['PLATINUM'] },
                '42d6e2db380e3906b00100f16668c257_page_1_RECOMMENDED_UK',
            ],
            [
                'Ring style: solitaire',
                { ringStyles: ['SOLITAIRE'] },
                '708ca8760a936d4095d378776c7bf462_page_1_RECOMMENDED_UK',
            ],
            [
                'Shape: round',
                { centreStoneShapes: ['ROUND'] },
                '9c0672f46b05e8ffbf768eb5a162514c_page_1_RECOMMENDED_UK',
            ],
            [
                'Design style: vintage',
                { designStyles: ['VINTAGE'] },
                'a5a416552bb3b590a4f1796af49490e6_page_1_RECOMMENDED_UK',
            ],
            [
                'Design features: eagle claws',
                { designFeatures: ['EAGLE_CLAWS'] },
                '44c05aa7c30da0eb920c9dc4756bc7ea_page_1_RECOMMENDED_UK',
            ],
            [
                'Metal: platinum, ring style: solitaire, shape: round, design style: vintage, design features: eagle claws',
                {
                    centreStoneShapes: ['ROUND'],
                    ringStyles: ['SOLITAIRE'],
                    designStyles: ['VINTAGE'],
                    designFeatures: ['EAGLE_CLAWS'],
                    primaryMetalCodes: ['PLATINUM'],
                },
                'f943aa73eedd0512f269572b31e1e052_page_1_RECOMMENDED_UK',
            ],
            [
                'Price: 2190-3491',
                { price: { min: '2190', max: '3491' } },
                '8e2a2b7b0859666504a0fd9f2576ba1e_page_1_RECOMMENDED_UK',
            ],
        ] as [string, CatalogueEngagementRingsFilterInput, string][])(
            '%s',
            ([, filterInput, id], { expect }) => {
                expect(
                    generateAnalyticsProductListId(
                        getAnalyticsProductListCategory([
                            {
                                '__typename': 'CatalogueEngagementRing',
                            },
                        ]),
                        filterInput,
                        CatalogueSortOrder.Recommended,
                        1,
                        'UK'
                    )
                ).toBe(id)
            }
        )
    })

    describe('Generate analytics PLP ID for Jewellery', () => {
        test.concurrent.for([
            [
                'Without filters',
                undefined,
                {},
                '7d61162466cc80e86ab961c91d8cee60_page_1_RECOMMENDED_UK',
            ],
            [
                'Metal: platinum',
                undefined,
                { primaryMetalCodes: ['PLATINUM'] },
                '6d848f4c97898d2fa3d33efb92da0139_page_1_RECOMMENDED_UK',
            ],
            [
                'Gemstone: diamond',
                undefined,
                { gemstones: ['DIAMOND_APR'] },
                '140bf9b469e3d0ab806ca51c6a09f508_page_1_RECOMMENDED_UK',
            ],
            [
                'Design style: bridal',
                undefined,
                { designStyles: ['BRIDAL'] },
                '0349fe5be820fceaab989eada5bdcb71_page_1_RECOMMENDED_UK',
            ],
            [
                'Design features: pave',
                undefined,
                { designFeatures: ['PAVE'] },
                'dfbad404ef36b40adc0bacb9fcc1beb0_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: necklace',
                'NECKLACE',
                {},
                '95659b4ef425ffbeb44a8c05c1123734_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: necklace, metal: platinum',
                'NECKLACE',
                { primaryMetalCodes: ['PLATINUM'] },
                '00af9c04c5396463bfe1871b42094aab_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: necklace, product style: solitaire',
                'NECKLACE',
                { productStyles: ['SOLITAIRE'] },
                '4d355a8ab4d53247b930d97f5c3de955_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: necklace, gemstone: diamond',
                'NECKLACE',
                { gemstones: ['DIAMOND_APR'] },
                '9fbd47840f2d7c73bb113c1a89dd54fc_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: necklace, design style: bridal',
                'NECKLACE',
                { designStyles: ['BRIDAL'] },
                '0d8c1c6ca046c290a6afe38459e9e4cb_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: necklace, design features: bezel setting',
                'NECKLACE',
                { designFeatures: ['BEZEL_SETTING'] },
                '45227b9a479f60531d23c2010904ec1b_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: bracelet',
                'BRACELET',
                {},
                'c4272fe6eca1c72c0294a36891c7bce5_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: bracelet, product style: tennis',
                'BRACELET',
                { productStyles: ['TENNIS'] },
                '57f7014dbb262eb56ab03b98f4ece7ae_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: earrings',
                'EARRINGS',
                {},
                '764c494ed074d757ff2f845b1658fd94_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: earrings, product style: studs',
                'EARRINGS',
                { productStyles: ['STUDS'] },
                '66a6574664f0a45490ced67761d8e45f_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: ring',
                'RING',
                {},
                '1914b2a837e9544df96b70f97a349c27_page_1_RECOMMENDED_UK',
            ],
            [
                'Category: ring, product style: promise',
                'RING',
                { productStyles: ['PROMISE'] },
                'c1c861e5ecdf5b3ae2e007d9feb4ab80_page_1_RECOMMENDED_UK',
            ],
        ] as [
            string,
            JewelleryProductType,
            (
                | CatalogueJewelleriesFilterInput
                | CatalogueJewelleriesBraceletsFilterInput
                | CatalogueJewelleriesEarringsFilterInput
                | CatalogueJewelleriesNecklacesFilterInput
                | CatalogueJewelleriesRingsFilterInput
            ),
            string,
        ][])('%s', ([, productType, filterInput, id], { expect }) => {
            expect(
                generateAnalyticsProductListId(
                    getAnalyticsProductListCategory(
                        [
                            // not relevant for jewellery but required by the function
                            {
                                '__typename': 'CatalogueJewelleryBracelet',
                            },
                        ],
                        {
                            productType,
                        }
                    ),
                    filterInput,
                    CatalogueSortOrder.Recommended,
                    1,
                    'UK'
                )
            ).toBe(id)
        })
    })

    describe('Generate analytics PLP ID for page number', () => {
        test.concurrent.for([
            [1, 'page_1'],
            [2, 'page_2'],
            [3, 'page_3'],
        ] as [number, string][])('Page %s', ([page, id], { expect }) => {
            expect(
                generateAnalyticsProductListId(
                    getAnalyticsProductListCategory([
                        {
                            '__typename': 'CatalogueWeddingRing',
                        },
                    ]),
                    {},
                    CatalogueSortOrder.Recommended,
                    page,
                    'UK'
                )
            ).toContain(id)
        })
    })

    describe('Generate analytics PLP ID for sorting', () => {
        test.concurrent.for([
            ['Recommended', CatalogueSortOrder.Recommended, 'RECOMMENDED'],
            [
                'Price ascending',
                CatalogueSortOrder.PriceAscending,
                'PRICE_ASCENDING',
            ],
            [
                'Price descending',
                CatalogueSortOrder.PriceDescending,
                'PRICE_DESCENDING',
            ],
        ] as [string, CatalogueSortOrder, string][])(
            '%s',
            ([, sortBy, id], { expect }) => {
                expect(
                    generateAnalyticsProductListId(
                        getAnalyticsProductListCategory([
                            {
                                '__typename': 'CatalogueWeddingRing',
                            },
                        ]),
                        {},
                        sortBy,
                        1,
                        'UK'
                    )
                ).toContain(id)
            }
        )
    })

    describe('Generate analytics PLP ID for different regions', () => {
        test.concurrent.for([
            ['UK', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_UK'],
            ['US', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_US'],
            ['AU', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_AU'],
            ['EU', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_EU'],
            ['CA', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_CA'],
            ['IE', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_IE'],
            ['NZ', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_NZ'],
            ['INT', 'f324debed0168be0a40bf6192c492186_page_1_RECOMMENDED_INT'],
        ] as [RegionCode, string][])(
            'Region: %s',
            ([region, id], { expect }) => {
                expect(
                    generateAnalyticsProductListId(
                        getAnalyticsProductListCategory([
                            {
                                '__typename': 'CatalogueWeddingRing',
                            },
                        ]),
                        {
                            'categories': [],
                            'ringStyles': [],
                            'primaryMetalCodes': [],
                            'price': {
                                'min': null,
                                'max': null,
                            },
                            'search': null,
                        },
                        CatalogueSortOrder.Recommended,
                        1,
                        region
                    )
                ).toBe(id)
            }
        )
    })
}
